import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  PageTabs,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useBusinessUnitsList, useBusinessUnitMutations } from "../api";
import {
  buildBusinessUnitBatchActions,
  type BusinessUnitTab,
} from "./batchActions";
import type { BusinessUnit } from "../types";

const PAGE_SIZE = 25;

const formatAddress = (b: BusinessUnit): string => {
  const line1 = [b.streetAddress, b.street2].filter(Boolean).join(", ");
  const line2 = [b.city, b.state].filter(Boolean).join(", ");
  return [line1, line2, b.postalCode].filter(Boolean).join(", ");
};

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<BusinessUnitTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useBusinessUnitMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useBusinessUnitsList({
      page: { number: pageNumber, size: pageSize },
      filter: { active: tab === "active" },
    });

  const selectedUnits = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<BusinessUnit>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "externalName",
      header: "External Name",
      cell: ({ row }) => row.original.externalName || "",
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ row }) => formatAddress(row.original),
    },
  ];

  const runOnSelected = async (
    fn: (b: BusinessUnit) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedUnits];
    let failures = 0;
    for (const b of targets) {
      try {
        await fn(b);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} business unit(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (b) => archive(b.id),
        (n) => `${n} business unit(s) archived.`
      );
    } else {
      await runOnSelected(
        (b) => restore(b.id, b.name),
        (n) => `${n} business unit(s) restored.`
      );
    }
  };

  const batchActions = buildBusinessUnitBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Business Units"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Business Unit"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as BusinessUnitTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No business units found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive business units?"
            : "Restore business units?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} business unit(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
